import {Dropdown, Form, MenuProps, Modal, Space, message} from 'antd';
import {
  UpdateWebhookInput,
  WebhookEventType,
  useDeleteWebhookMutation,
  useUpdateWebhookMutation
} from 'api/generated';
import {Formik, FormikHelpers} from 'formik';
import React, {useState} from 'react';
import {DeleteOutlined, DownOutlined, EditOutlined} from '@ant-design/icons';
import {webhooksQuery} from 'api/queries';
import {FormItem, Select, Switch} from 'formik-antd';
import {UrlInput} from 'components/Form/UrlInput';
import * as yup from 'yup';
import {getWebhookEventTypes} from '../../lib/eventUtils';

const {confirm} = Modal;

export const WebhookActions: React.FC<any> = ({accountId, webhook, masterAccount = false}) => {
  const [isOpen, setOpen] = useState(false);
  const [deleteWebhook] = useDeleteWebhookMutation({
    updateQuery: {query: webhooksQuery, variables: {accountId}}
  });
  const [updateWebhook] = useUpdateWebhookMutation({
    updateQuery: {query: webhooksQuery, variables: {accountId}}
  });

  const webhookEventTypes = getWebhookEventTypes();

  const handleSubmit = async (
    input: UpdateWebhookInput,
    {setSubmitting}: FormikHelpers<UpdateWebhookInput>
  ) => {
    try {
      await updateWebhook({
        variables: {
          input: {
            ...input,
            masterAccount
          }
        }
      });
      message.success('Webhook updated successfully.');
      setOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    confirm({
      title: 'Are you sure?',
      okText: 'Delete webhook',
      okType: 'danger',
      content: 'This action will completely delete the webhook.',
      onOk: async () => {
        await deleteWebhook({
          variables: {accountId: accountId, id: webhook.id},
          mapResultToUpdate: () => webhook
        });
        message.success('Webhook deleted successfully.');
      }
    });
  };

  const menuItems: MenuProps['items'] = [
    {
      key: 'update',
      icon: <EditOutlined />,
      label: 'Edit',
      onClick: () => setOpen(true)
    },
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      label: 'Delete',
      onClick: () => handleDelete()
    }
  ];

  const validationSchema = yup.object().shape({
    url: yup.string().url('URL is not valid').required('URL is required'),
    eventTypes: yup
      .array()
      .of(
        yup
          .string()
          .oneOf(Object.keys(WebhookEventType))
          .required('At least one event type is required')
      )
      .min(1, 'At least one event type is required'),
    enabled: yup.boolean().required('Enabled status is required')
  });

  return (
    <>
      <Dropdown menu={{items: menuItems}} trigger={['click']}>
        <a onClick={(e) => e.preventDefault()}>
          <Space>
            Actions
            <DownOutlined />
          </Space>
        </a>
      </Dropdown>
      <Formik<UpdateWebhookInput>
        onSubmit={handleSubmit}
        enableReinitialize
        initialValues={{
          accountId: accountId,
          id: webhook.id,
          url: webhook.url,
          eventTypes: webhook.eventTypes,
          enabled: webhook.enabled
        }}
        validationSchema={validationSchema}>
        {({handleSubmit, isSubmitting, resetForm}) => (
          <Modal
            open={isOpen}
            destroyOnClose
            afterClose={resetForm}
            title={'Update webhook'}
            okText={'Update'}
            confirmLoading={isSubmitting}
            onOk={() => handleSubmit()}
            onCancel={() => setOpen(false)}>
            <Form layout="vertical">
              <FormItem name="url" label={'URL'}>
                <UrlInput name="url" placeholder={'example.com'} />
              </FormItem>
              <FormItem name="eventTypes" label={'Event Types'}>
                <Select
                  mode="multiple"
                  allowClear
                  placeholder={'Please select the event types'}
                  name="eventTypes"
                  options={webhookEventTypes}
                  optionFilterProp="label"
                />
              </FormItem>
              <FormItem name="enabled" label={'Enabled'}>
                <Switch name="enabled" />
              </FormItem>
              <input type="submit" value={'submit'} hidden />
            </Form>
          </Modal>
        )}
      </Formik>
    </>
  );
};
